// 표준화된 SCATTER 차트 옵션 생성
import { formatValue } from '../../../utils/numberFormatter';
import { applyCommonStyles } from './applyCommonStyles';

export const generateScatterChartOption = (data, baseOption, options = {}) => {
  // 표준 구조: { series: [{ name: "...", values: [[x1,y1], [x2,y2], ...] }] }
  const series = data.series || [];
  
  // 차트 크기에 따른 동적 nameGap 계산 (스캐터 차트는 더 타이트하게)
  const containerWidth = options.__containerWidth || 500;
  const containerHeight = options.__containerHeight || 400;
  const dynamicYNameGap = containerWidth < 400 ? 30 : (containerWidth < 600 ? 35 : 40);
  const dynamicXNameGap = containerHeight < 300 ? 18 : (containerHeight < 400 ? 22 : 28);
  
  // X축 설정 (스캐터는 value 축)
  baseOption.xAxis = {
    ...baseOption.xAxis,
    type: 'value',
    name: options.axes?.x || '',
    nameLocation: 'middle',
    nameGap: dynamicXNameGap,
    nameTextStyle: {
      fontSize: 18,
      fontWeight: '600',
      color: '#2c3e50',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
    },
    axisLabel: {
      formatter: function(value) {
        return formatValue(value);
      }
    },
    scale: true // 스케일 자동 조정
  };

  // Y축 설정
  baseOption.yAxis = {
    ...baseOption.yAxis,
    type: 'value',
    name: options.axes?.y || '',
    nameLocation: 'middle',
    nameGap: dynamicYNameGap,
    nameTextStyle: {
      fontSize: 18,
      fontWeight: '600',
      color: '#2c3e50',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
    },
    axisLabel: {
      formatter: function(value) {
        return formatValue(value);
      }
    },
    scale: true
  };

  // containLabel: true가 Y축 제목 길이를 자동으로 측정하여 여백을 계산하므로
  // 하드코딩된 grid.left 설정은 제거 (근본적 해결)

  // 시리즈 설정
  const showDataLabels = options.advanced?.showDataLabels === true;
  const labelFormat = options.advanced?.labelFormat || '({x}, {y})';
  const makeScatterLabelFormatter = () => (params) => {
    if (Array.isArray(params.value)) {
      const x = formatValue(params.value[0]);
      const y = formatValue(params.value[1]);
      return labelFormat.replace('{x}', x).replace('{y}', y);
    }
    return formatValue(params.value);
  };

  baseOption.series = series.map((seriesItem, index) => {
    const colors = options.style?.colors || ['#4CAF50', '#2196F3', '#FFC107', '#E91E63', '#9C27B0'];
    return {
      name: seriesItem.name || '데이터',
      type: 'scatter',
      data: seriesItem.values || [],
      symbolSize: (val) => {
        // 값이 [x, y, size] 형태면 size 사용, 아니면 기본값
        if (Array.isArray(val) && val.length > 2 && typeof val[2] === 'number') {
          return Math.max(5, val[2]);
        }
        return 10; // 기본 크기
      },
      itemStyle: {
        color: colors[index % colors.length]
      },
      emphasis: {
        focus: 'series',
        label: {
          show: showDataLabels,
          formatter: makeScatterLabelFormatter(),
          position: options.advanced?.labelPosition || 'top'
        }
      }
    };
  });

  // 범례 설정 - showLegend 옵션이 true이거나 다중 시리즈인 경우 표시
  const showLegend = options.advanced?.showLegend === true || series.length > 1;
  
  if (showLegend || options.advanced?.legendPosition) {
    // 범례 위치 옵션 처리
    const legendPosition = options.advanced?.legendPosition || 'bottom';
    
    // 범례 순서 설정 (legendOrder가 있으면 해당 순서로, 없으면 series 순서대로)
    let legendData;
    if (options.advanced?.legendOrder && Array.isArray(options.advanced.legendOrder)) {
      // 사용자 지정 순서가 있는 경우
      const seriesMap = new Map(series.map(s => [s.name || '데이터', s]));
      legendData = options.advanced.legendOrder.filter(name => seriesMap.has(name));
      // legendOrder에 없는 시리즈들은 뒤에 추가
      const remainingSeries = series.filter(s => !options.advanced.legendOrder.includes(s.name || '데이터'));
      legendData = [...legendData, ...remainingSeries.map(s => s.name || '데이터')];
    } else {
      // 기본: series 순서대로
      legendData = series.map(s => s.name || '데이터');
    }
    
    const legendConfig = {
      data: legendData,
      type: 'scroll'
    };

    // 위치별 설정 - 각 케이스에서 모든 방향 속성을 명시적으로 설정
    switch(legendPosition) {
      case 'top':
        legendConfig.top = 10;
        legendConfig.bottom = undefined;
        legendConfig.left = 'center';
        legendConfig.right = undefined;
        legendConfig.orient = 'horizontal';
        break;
      case 'bottom':
        legendConfig.top = undefined;
        legendConfig.bottom = 10;
        legendConfig.left = 'center';
        legendConfig.right = undefined;
        legendConfig.orient = 'horizontal';
        break;
      case 'left':
        legendConfig.top = 'middle';
        legendConfig.bottom = undefined;
        legendConfig.left = 10;
        legendConfig.right = undefined;
        legendConfig.orient = 'vertical';
        break;
      case 'right':
        legendConfig.top = 'middle';
        legendConfig.bottom = undefined;
        legendConfig.left = undefined;
        legendConfig.right = 10;
        legendConfig.orient = 'vertical';
        break;
      case 'top-right':
        legendConfig.top = '5%';
        legendConfig.right = '5%';
        legendConfig.orient = 'vertical';
        legendConfig.backgroundColor = 'rgba(255, 255, 255, 0.9)';
        legendConfig.borderColor = '#ddd';
        legendConfig.borderWidth = 1;
        legendConfig.borderRadius = 4;
        legendConfig.padding = [5, 10];
        legendConfig.itemGap = 5;
        break;
      case 'top-left':
        legendConfig.top = '5%';
        legendConfig.left = '5%';
        legendConfig.orient = 'vertical';
        legendConfig.backgroundColor = 'rgba(255, 255, 255, 0.9)';
        legendConfig.borderColor = '#ddd';
        legendConfig.borderWidth = 1;
        legendConfig.borderRadius = 4;
        legendConfig.padding = [5, 10];
        legendConfig.itemGap = 5;
        break;
    }

    baseOption.legend = legendConfig;

    // 범례 위치에 따른 grid 여백 조정 (범례와 차트 영역 겹침 방지)
    if (legendPosition === 'right') {
      baseOption.grid = { ...baseOption.grid, right: (baseOption.grid?.right || 30) + 80 };
    } else if (legendPosition === 'left') {
      baseOption.grid = { ...baseOption.grid, left: (baseOption.grid?.left || 50) + 80 };
    } else if (legendPosition === 'top' || legendPosition === 'top-left' || legendPosition === 'top-right') {
      baseOption.grid = { ...baseOption.grid, top: (baseOption.grid?.top || 30) + 30 };
    }
  } else {
    delete baseOption.legend;
  }

  // 툴팁 순서 설정 (legendOrder와 동일하게)
  if (options.advanced?.legendOrder && Array.isArray(options.advanced.legendOrder) && series.length > 1) {
    baseOption.tooltip = {
      ...baseOption.tooltip,
      formatter: function(params) {
        if (!Array.isArray(params)) params = [params];
        
        let tooltipHtml = '';
        if (params[0] && params[0].name) {
          tooltipHtml += `${params[0].name}<br/>`;
        }
        
        // legendOrder 순서대로 정렬
        const seriesMap = new Map(series.map(s => [s.name || '데이터', s]));
        const orderedSeriesNames = options.advanced.legendOrder.filter(name => seriesMap.has(name));
        const remainingSeriesNames = series.filter(s => !options.advanced.legendOrder.includes(s.name || '데이터')).map(s => s.name || '데이터');
        const allOrderedNames = [...orderedSeriesNames, ...remainingSeriesNames];
        
        // 순서대로 툴팁 항목 생성
        allOrderedNames.forEach(seriesName => {
          const param = params.find(p => p.seriesName === seriesName);
          if (param) {
            const value = param.value;
            let formattedValue = '';
            if (Array.isArray(value) && value.length >= 2) {
              // 스캐터 차트의 경우 [x, y] 또는 [x, y, size] 형태
              formattedValue = `(${formatValue(value[0])}, ${formatValue(value[1])})`;
              if (value.length > 2) {
                formattedValue += ` 크기: ${formatValue(value[2])}`;
              }
            } else {
              formattedValue = formatValue(value);
            }
            tooltipHtml += `${param.marker}${param.seriesName}: <b>${formattedValue}</b><br/>`;
          }
        });
        
        return tooltipHtml;
      }
    };
  }

  // 공통 스타일 적용
  if (options.style) {
    baseOption = applyCommonStyles(baseOption, options.style);
  }

  // 사용자가 advanced.grid로 직접 여백을 지정한 경우 최종 오버라이드
  if (options.advanced?.grid && typeof options.advanced.grid === 'object') {
    baseOption.grid = { ...baseOption.grid, ...options.advanced.grid };
  }

  // transparent 처리 (ECharts 차트 배경 투명도)
  // showFrame은 DashboardComponentRenderer에서 처리하므로 여기서는 transparent만 처리
  if ((options.transparent === true || options.transparent === 'true')) {
    // ECharts 전체 배경과 그리드 배경을 모두 투명하게 설정
    baseOption.backgroundColor = 'transparent';
    if (baseOption.grid) {
      baseOption.grid.backgroundColor = 'transparent';
    }
  }

  return baseOption;
}; 